import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Page,
  Select,
  Spinner,
  Text,
} from '@shopify/polaris';
import type { BadgeProps } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import type { EventType, SigningEvent } from '../types';

// ---------------------------------------------------------------------------
// Event display config
// ---------------------------------------------------------------------------

interface EventConfig {
  icon: string;
  label: string;
  tone: BadgeProps['tone'];
}

const EVENT_CONFIG: Partial<Record<EventType, EventConfig>> = {
  SESSION_CREATED:   { icon: '📝', label: 'Sent',          tone: undefined },
  EMAIL_SENT:        { icon: '📧', label: 'Email sent',     tone: undefined },
  REMINDER_SENT:     { icon: '🔔', label: 'Reminder sent',  tone: undefined },
  LINK_OPENED:       { icon: '🔗', label: 'Opened',         tone: 'info' },
  DOCUMENT_VIEWED:   { icon: '👁', label: 'Viewed',         tone: 'info' },
  DOCUMENT_SIGNED:   { icon: '✅', label: 'Signed',         tone: 'success' },
  DOCUMENT_DECLINED: { icon: '❌', label: 'Declined',       tone: 'critical' },
  SESSION_EXPIRED:   { icon: '⏰', label: 'Expired',        tone: 'warning' },
  SESSION_CANCELLED: { icon: '🚫', label: 'Cancelled',      tone: 'critical' },
};

const defaultConfig: EventConfig = { icon: '•', label: 'Event', tone: undefined };

function cfg(type: EventType): EventConfig {
  return EVENT_CONFIG[type] ?? defaultConfig;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

export default function ActivityPage() {
  const { request } = useApi();
  const navigate = useNavigate();

  const [events, setEvents] = useState<SigningEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const load = useCallback(
    async (newFilter: string, newOffset: number, append: boolean) => {
      if (newOffset === 0) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const qs = [
          newFilter ? `eventType=${newFilter}` : '',
          `limit=${PAGE_SIZE}`,
          `offset=${newOffset}`,
        ]
          .filter(Boolean)
          .join('&');
        const data = await request<SigningEvent[]>(`/sessions/events?${qs}`);
        setEvents((prev) => (append ? [...prev, ...data] : data));
        setHasMore(data.length === PAGE_SIZE);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [request],
  );

  useEffect(() => {
    setOffset(0);
    load(filter, 0, false);
  }, [filter, load]);

  const loadMore = () => {
    const next = offset + PAGE_SIZE;
    setOffset(next);
    load(filter, next, true);
  };

  const filterOptions = [
    { label: 'All activity', value: '' },
    { label: 'Signed', value: 'DOCUMENT_SIGNED' },
    { label: 'Declined', value: 'DOCUMENT_DECLINED' },
    { label: 'Viewed', value: 'DOCUMENT_VIEWED' },
    { label: 'Opened', value: 'LINK_OPENED' },
    { label: 'Expired', value: 'SESSION_EXPIRED' },
  ];

  return (
    <Page title="Activity">
      <BlockStack gap="400">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

        <InlineStack align="end">
          <div style={{ width: 200 }}>
            <Select
              label="Filter"
              labelInline
              options={filterOptions}
              value={filter}
              onChange={(v) => { setFilter(v); }}
            />
          </div>
        </InlineStack>

        <Card padding="0">
          {loading ? (
            <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
              <Spinner />
            </div>
          ) : events.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <Text tone="subdued" as="p">No activity yet.</Text>
            </div>
          ) : (
            <div>
              {events.map((event, i) => {
                const { icon, label, tone } = cfg(event.eventType);
                const sessionName = event.session?.signerName ?? 'Unknown signer';
                const templateName = event.session?.template?.name ?? '';
                const orderNum = event.session?.shopifyOrderNumber;

                return (
                  <div key={event.id}>
                    {i > 0 && <Divider />}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => event.session && navigate(`/sessions/${event.session.id}`)}
                      onKeyDown={(e) => e.key === 'Enter' && event.session && navigate(`/sessions/${event.session.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '14px 16px',
                        cursor: event.session ? 'pointer' : 'default',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => { if (event.session) (e.currentTarget as HTMLDivElement).style.background = '#f9fafb'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                    >
                      <span style={{ fontSize: '18px', lineHeight: 1, marginTop: '2px', flexShrink: 0 }}>
                        {icon}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <InlineStack gap="200" blockAlign="center" wrap>
                          <Badge tone={tone}>{label}</Badge>
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {sessionName}
                          </Text>
                          {templateName && (
                            <Text as="span" variant="bodyMd" tone="subdued">
                              · {templateName}
                            </Text>
                          )}
                          {orderNum && (
                            <Text as="span" variant="bodySm" tone="subdued">
                              · #{orderNum}
                            </Text>
                          )}
                        </InlineStack>
                        <InlineStack gap="300" wrap>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {formatAbsolute(event.occurredAt)} ({relativeTime(event.occurredAt)})
                          </Text>
                          {event.ipAddress && (
                            <Text as="span" variant="bodySm" tone="subdued">
                              IP: {event.ipAddress}
                            </Text>
                          )}
                        </InlineStack>
                      </div>
                      {event.session && (
                        <Text as="span" variant="bodySm" tone="subdued" breakWord={false}>
                          ›
                        </Text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {!loading && hasMore && (
          <InlineStack align="center">
            <Button loading={loadingMore} onClick={loadMore}>Load more</Button>
          </InlineStack>
        )}
      </BlockStack>
    </Page>
  );
}
